import '@datadog/browser-logs/bundle/datadog-logs';
import { getPlatformMeta } from './platformMeta';
import { useEffect } from 'react';
import { App as capacitorApp } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { isNative } from '@/domains/shared/utils/native';

type DatadogLogEvent = {
  message?: string;
  [key: string]: unknown;
};

const appInfo = {
  appVersion: 'unknown',
  buildNumber: 'unknown',
  liveUpdateVersion: 'unknown',
};

async function initializeAppInfo() {
  if (!isNative()) return;

  const { version, build } = await capacitorApp.getInfo();
  appInfo.appVersion = version;
  appInfo.buildNumber = build;

  try {
    const { bundleId } = await LiveUpdate.getCurrentBundle();
    appInfo.liveUpdateVersion = bundleId ?? 'unknown';
  } catch {
    appInfo.liveUpdateVersion = 'unknown';
  }
}

export const useDatadogLogs = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    initializeAppInfo();

    const platformMeta = getPlatformMeta();

    // Enhanced configuration for both web and native platforms
    const datadogConfig = {
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT!,
      site: 'datadoghq.eu',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: `${platformMeta.app_name}-${platformMeta.app_region}`,
      version: process.env.NEXT_PUBLIC_RELEASE,
      env: platformMeta.app_env,
      beforeSend: (log: DatadogLogEvent) => {
        const message = log?.message;
        if (
          typeof message === 'string' &&
          (message.includes('Invalid font data in ArrayBuffer.') ||
            message.includes('[INTERCOM GTM] Failed to fetch token: ') ||
            message.includes('XHR error POST https://') ||
            message.includes('XHR error GET https://') ||
            message.includes(
              'Fetch error POST https://c.pensionbee.com/com.snowplowanalytics.snowplow/tp2',
            ) ||
            message.includes(
              'Fetch error POST https://px.ads.linkedin.com/wa/?medium=',
            ) ||
            message.includes(
              'Fetch error POST https://pagead2.googlesyndication.com/',
            ) ||
            message.includes(
              'Fetch error POST https://srm.aa.contentsquare.net/',
            ) ||
            message.includes(
              'Fetch error POST https://www.google-analytics.com/',
            ) ||
            message.includes('Fetch error POST https://consent') ||
            message.includes('https://region1.google-analytics.com'))
        ) {
          return false;
        }
        // Add native platform context to all logs
        log.platform = platformMeta.app_platform;
        log.isNative = isNative();
        log.userAgent = navigator.userAgent;
        log.timestamp = new Date().toISOString();
        log.appVersion = appInfo.appVersion;
        log.buildNumber = appInfo.buildNumber;
        log.liveUpdateVersion = appInfo.liveUpdateVersion;
      },
    };

    window.DD_LOGS?.init(datadogConfig);
  }, []);
};

type SetDatadogUserArgs = {
  id?: string;
  email?: string;
};

export const updateDatadogUser = (user: SetDatadogUserArgs | null) => {
  const datadog = typeof window !== 'undefined' ? window.DD_LOGS : undefined;
  if (!datadog) return;

  if (user?.id || user?.email) {
    datadog.setUser({ id: user.id, email: user.email });
    console.debug('Datadog user set', { id: user.id, email: user.email });
  } else {
    datadog.clearUser();
    console.debug('Datadog user cleared (not logged in)');
  }
};

export const logToDatadog = (
  level: 'info' | 'warn' | 'error' | 'debug',
  message: string,
  data?: Record<string, unknown>,
) => {
  if (typeof window === 'undefined') return;

  const platformMeta = getPlatformMeta();

  // Enhanced data with platform context
  const enhancedData = {
    ...data,
    platform: platformMeta.app_platform,
    isNative: isNative(),
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  };

  window.DD_LOGS?.logger?.[level]?.(`GTMWithConsent: ${message}`, enhancedData);
};
